import { defineStore } from 'pinia'
import { useGlobalStore } from 'src/CrudManager/Core/stores/useGlobalStore'
import { ref } from 'vue'

interface Setting {
  id: string
  name: string
  value: string | number | boolean | object | null
  type: string
  description: string
  group: string
  options?: string[] | number[] | boolean[]
  default?: string | number | boolean | object | null
  validation?: string
  icon?: string
}

export const useServerSettingsStore = defineStore(
  'server-settings-store',
  () => {
    const settings = ref<Setting[]>([])

    const fetchSettings = async () => {
      const globalStore = useGlobalStore()

      globalStore.syncAppData()
    }

    const getSetting = (name: string) => {
      return settings.value.find((setting) => setting.name === name)
    }

    const getSettingValue = (name: string) => {
      const setting = getSetting(name)
      if (!setting) {
        return null
      }
      return setting.value
    }

    const getSettingValueAsJson = <T>(name: string, fallback?: T): T | null => {
      const setting = getSetting(name)
      if (!setting?.value) {
        return fallback || null
      }

      try {
        // If the value is already an object, return it directly
        if (typeof setting.value === 'object' && setting.value !== null) {
          return setting.value as T
        }

        // If it's a string, try to parse it as JSON
        if (typeof setting.value === 'string') {
          return JSON.parse(setting.value) as T
        }

        // For other types, return as-is
        return setting.value as T
      } catch (error) {
        console.warn(`Failed to parse setting '${name}' as JSON:`, error)
        return fallback || null
      }
    }

    return {
      settings,
      fetchSettings,
      getSetting,
      getSettingValue,
      getSettingValueAsJson,
    }
  },
  { persist: true },
)
